import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Edit3, Plus, Save, X, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const STATUS_COLORS = {
  approved: "text-[var(--emerald)] bg-[var(--emerald)]/10",
  pending: "text-[var(--gold)] bg-[var(--gold)]/10",
  hidden: "text-[var(--muted)] bg-black/5",
};

const EMPTY = {
  title: "",
  property_type: "Villa",
  city: "",
  country: "",
  address: "",
  bedrooms: 2,
  bathrooms: 2,
  max_guests: 4,
  nightly_rate_usd: 0,
  weekly_rate_usd: 0,
  monthly_rate_usd: 0,
  amenities: [],
  photos: [],
  description: "",
  host_name: "",
  host_email: "",
  host_phone: "",
  status: "approved",
};

/**
 * Admin Listings — manual create / edit / delete for property listings.
 *
 * Use this page to pre-populate listing pages before the Hosts portal is
 * launched, or to curate / hide listings that HubSpot sync brought in.
 *
 * Routes: GET /api/listings/all, POST /api/listings, PUT /api/listings/:id,
 *         DELETE /api/listings/:id
 */
export default function AdminListingsPage() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/listings/all?limit=500");
      setListings(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load listings.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    load();
  }, [user]);

  if (!user) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <p className="text-[var(--ink-2)]">Please <Link to="/login" className="underline text-[var(--brand-red)]">sign in</Link> as an admin to manage listings.</p>
      </div>
    );
  }
  if (user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <p className="text-[var(--ink-2)]">This page is for admins only.</p>
      </div>
    );
  }

  const startEdit = (l) => {
    setEditId(l.id);
    setForm({
      ...EMPTY,
      ...l,
      amenities: l.amenities || [],
      photos: l.photos || [],
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const startNew = () => {
    setEditId(null);
    setForm(EMPTY);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancel = () => {
    setEditId(null);
    setForm(EMPTY);
    setShowForm(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        bedrooms: form.bedrooms === "" ? null : Number(form.bedrooms),
        bathrooms: form.bathrooms === "" ? null : Number(form.bathrooms),
        max_guests: form.max_guests === "" ? null : Number(form.max_guests),
        nightly_rate_usd: form.nightly_rate_usd === "" ? null : Number(form.nightly_rate_usd),
        weekly_rate_usd: form.weekly_rate_usd === "" ? null : Number(form.weekly_rate_usd),
        monthly_rate_usd: form.monthly_rate_usd === "" ? null : Number(form.monthly_rate_usd),
      };
      if (editId) {
        await api.put(`/listings/${editId}`, payload);
        toast.success("Listing updated.");
      } else {
        await api.post("/listings", payload);
        toast.success("Listing created.");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this listing? This cannot be undone.")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing deleted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const toggleStatus = async (l) => {
    const next = l.status === "approved" ? "hidden" : "approved";
    try {
      await api.put(`/listings/${l.id}`, { status: next });
      toast.success(`Listing ${next === "approved" ? "shown" : "hidden"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Status update failed.");
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12" data-testid="admin-listings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/admin" className="text-[11px] uppercase tracking-[0.2em] text-[var(--brand-red)] hover:opacity-80 inline-flex items-center gap-1">
            <ArrowLeft size={12} /> Back to admin
          </Link>
          <h1 className="font-display text-4xl text-[var(--emerald)] mt-2">Property Listings</h1>
          <p className="text-sm text-[var(--ink-2)] mt-1">Manually create or update properties shown on the public Stays catalog.</p>
        </div>
        {!showForm && (
          <button
            onClick={startNew}
            className="btn-emerald inline-flex items-center gap-2"
            data-testid="admin-listings-new"
          >
            <Plus size={14} /> New listing
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={submit} className="lux-card mb-10" data-testid="admin-listings-form">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-display text-2xl text-[var(--emerald)]">
              {editId ? "Edit listing" : "New listing"}
            </h2>
            <button type="button" onClick={cancel} className="text-[var(--muted)] hover:text-[var(--brand-red)]" data-testid="admin-listings-cancel">
              <X size={18} />
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Title *" value={form.title} onChange={(v) => setForm({ ...form, title: v })} testid="field-title" required />
            <Field label="Property type *" value={form.property_type} onChange={(v) => setForm({ ...form, property_type: v })} testid="field-type" required />
            <Field label="City *" value={form.city} onChange={(v) => setForm({ ...form, city: v })} testid="field-city" required />
            <Field label="Country" value={form.country} onChange={(v) => setForm({ ...form, country: v })} testid="field-country" />
            <Field label="Address" value={form.address} onChange={(v) => setForm({ ...form, address: v })} testid="field-address" colspan="2" />

            <Field label="Bedrooms" type="number" value={form.bedrooms} onChange={(v) => setForm({ ...form, bedrooms: v })} testid="field-bedrooms" />
            <Field label="Bathrooms" type="number" step="0.5" value={form.bathrooms} onChange={(v) => setForm({ ...form, bathrooms: v })} testid="field-bathrooms" />
            <Field label="Max guests" type="number" value={form.max_guests} onChange={(v) => setForm({ ...form, max_guests: v })} testid="field-guests" />
            <div />

            <Field label="Nightly rate (USD)" type="number" value={form.nightly_rate_usd} onChange={(v) => setForm({ ...form, nightly_rate_usd: v })} testid="field-nightly" />
            <Field label="Weekly rate (USD)" type="number" value={form.weekly_rate_usd} onChange={(v) => setForm({ ...form, weekly_rate_usd: v })} testid="field-weekly" />
            <Field label="Monthly rate (USD)" type="number" value={form.monthly_rate_usd} onChange={(v) => setForm({ ...form, monthly_rate_usd: v })} testid="field-monthly" />
            <div />

            <ListField label="Amenities (one per line)" value={form.amenities} onChange={(v) => setForm({ ...form, amenities: v })} testid="field-amenities" />
            <ListField label="Photo URLs (one per line)" value={form.photos} onChange={(v) => setForm({ ...form, photos: v })} testid="field-photos" />

            <TextareaField label="Description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} testid="field-description" colspan="2" />

            <Field label="Host name" value={form.host_name} onChange={(v) => setForm({ ...form, host_name: v })} testid="field-host-name" />
            <Field label="Host email" type="email" value={form.host_email} onChange={(v) => setForm({ ...form, host_email: v })} testid="field-host-email" />
            <Field label="Host phone" value={form.host_phone} onChange={(v) => setForm({ ...form, host_phone: v })} testid="field-host-phone" />
            <SelectField label="Status" value={form.status} onChange={(v) => setForm({ ...form, status: v })} options={["approved", "pending", "hidden"]} testid="field-status" />
          </div>

          <div className="mt-6 flex items-center gap-3">
            <button type="submit" className="btn-emerald inline-flex items-center gap-2" data-testid="admin-listings-save">
              <Save size={14} /> {editId ? "Save changes" : "Create listing"}
            </button>
            <button type="button" onClick={cancel} className="text-[11px] uppercase tracking-[0.2em] text-[var(--muted)] hover:text-[var(--brand-red)]">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="lux-card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl text-[var(--emerald)]">
            All listings {!loading && <span className="text-[var(--muted)] text-sm">({listings.length})</span>}
          </h2>
        </div>
        {loading ? (
          <div className="text-[var(--muted)]">Loading…</div>
        ) : listings.length === 0 ? (
          <div className="text-[var(--muted)]">No listings yet. Click <strong>New listing</strong> to add the first one.</div>
        ) : (
          <ul className="divide-y divide-[var(--emerald)]/10">
            {listings.map((l) => (
              <li key={l.id} className="py-4 flex items-start gap-4" data-testid={`listing-row-${l.id}`}>
                <div className="w-20 h-20 bg-[var(--emerald)]/5 rounded shrink-0 overflow-hidden">
                  {l.photos?.[0] && (
                    <img src={l.photos[0]} alt={l.title} className="w-full h-full object-cover" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display text-lg text-[var(--emerald)] truncate">{l.title}</div>
                    <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-0.5 ${STATUS_COLORS[l.status] || ""}`}>{l.status}</span>
                  </div>
                  <div className="text-sm text-[var(--ink-2)] mt-1">
                    {l.property_type} · {l.city}{l.country ? `, ${l.country}` : ""} · {l.bedrooms ?? "—"}bd / {l.bathrooms ?? "—"}ba · sleeps {l.max_guests ?? "—"}
                  </div>
                  <div className="text-xs text-[var(--muted)] mt-0.5">
                    {l.nightly_rate_usd != null && `$${l.nightly_rate_usd}/night`} {l.host_name && `· host: ${l.host_name}`}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => toggleStatus(l)} className="p-2 text-[var(--muted)] hover:text-[var(--emerald)]" title={l.status === "approved" ? "Hide" : "Show"} data-testid={`listing-toggle-${l.id}`}>
                    {l.status === "approved" ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                  <button onClick={() => startEdit(l)} className="p-2 text-[var(--muted)] hover:text-[var(--brand-red)]" title="Edit" data-testid={`listing-edit-${l.id}`}>
                    <Edit3 size={16} />
                  </button>
                  <button onClick={() => remove(l.id)} className="p-2 text-[var(--muted)] hover:text-[var(--brand-red)]" title="Delete" data-testid={`listing-delete-${l.id}`}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid, colspan, step }) => (
  <label className={`block ${colspan === "2" ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1">{label}</span>
    <input
      type={type}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      step={step}
      className="w-full bg-[var(--ivory)] border border-[var(--emerald)]/20 px-3 py-2 text-[var(--emerald)] focus:outline-none focus:border-[var(--gold)]"
      data-testid={testid}
    />
  </label>
);

const TextareaField = ({ label, value, onChange, testid, colspan }) => (
  <label className={`block ${colspan === "2" ? "md:col-span-2" : ""}`}>
    <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1">{label}</span>
    <textarea
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value)}
      rows={4}
      className="w-full bg-[var(--ivory)] border border-[var(--emerald)]/20 px-3 py-2 text-[var(--emerald)] focus:outline-none focus:border-[var(--gold)]"
      data-testid={testid}
    />
  </label>
);

const ListField = ({ label, value, onChange, testid }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1">{label}</span>
    <textarea
      value={(value || []).join("\n")}
      onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))}
      rows={4}
      className="w-full bg-[var(--ivory)] border border-[var(--emerald)]/20 px-3 py-2 text-[var(--emerald)] focus:outline-none focus:border-[var(--gold)]"
      data-testid={testid}
    />
  </label>
);

const SelectField = ({ label, value, onChange, options, testid }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.2em] text-[var(--muted)] mb-1">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[var(--ivory)] border border-[var(--emerald)]/20 px-3 py-2 text-[var(--emerald)] focus:outline-none focus:border-[var(--gold)]"
      data-testid={testid}
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);
